import React, { useState, useEffect, useRef, useCallback } from "react";
import { Link } from "react-router-dom";
import MultiAddress from "../../../components/ui/forms/address/MultiAddress";
import SingleAddress from "../../../components/ui/forms/address/SingleAddress";
import ComposedInputToggle from "../../../components/ui/forms/ComposedInputToggle";
import MultiSelect from "../../../components/ui/forms/MultiSelect";
import RadioGroup from "../../../components/ui/forms/RadioGroup";
import { getSelectOptions } from "../../selectHelpers";
import ErrorLabel from "../../../components/ui/forms/base/ErrorLabel";
import ModalNew from "../../../components/ui/popup/Modal";
import { PopupHeader, PopupContent } from "../../../components/ui/popup/common";
import modalClasses from "../../../components/ui/popup/Modal.module.css";
import DateInput from "../../../components/ui/forms/DateInput";
import FieldLabel from "../../../components/ui/forms/FieldLabel";
import Tooltip from "../../../components/ui/Tooltip";
import {
  formatPhoneInput,
  cleanPhoneNumber,
} from "../../formatting/phoneFormatter";
import PhoneInput from "../../../components/ui/forms/PhoneInput";

// Password field component that can use hooks
const PasswordField = ({
  field,
  fieldName,
  formState,
  localHandleChange,
  formMethods,
  wrapperStyle,
  hasAttemptedNext = false,
}) => {
  const [showPassword, setShowPassword] = useState(false);
  const [passwordMatchError, setPasswordMatchError] = useState("");
  const [validations, setValidations] = useState({
    length: false,
    letter: false,
    number: false,
    special: false,
  });

  const handleErrorChange = useCallback((error) => {
    setPasswordMatchError(error);
  }, []);

  const validatePassword = useCallback((value) => {
    setValidations({
      length: value.length >= 8,
      letter: /[a-zA-Z]/.test(value),
      number: /\d/.test(value),
      special: /[!@#$%^&*(),.?":{}|<>]/.test(value),
    });
  }, []);

  return (
    <div key={fieldName} className="form-field" style={wrapperStyle}>
      {!field.hideLabel && (
        <FieldLabel
          label={field.label}
          description={field.description}
          htmlFor={fieldName}
          required={field.required || false}
        />
      )}
      <div className="password-input-container">
        <input
          type={showPassword ? "text" : "password"}
          name={fieldName}
          className="form-input"
          placeholder={field.placeholder || ""}
          required={field.required || false}
          pattern={field.input_pattern || undefined}
          minLength={field.minLength || undefined}
          maxLength={field.maxLength || undefined}
          {...(formMethods
            ? formMethods.register(fieldName, {
                required: field.required || false,
                pattern: field.input_pattern
                  ? new RegExp(field.input_pattern)
                  : undefined,
                minLength: field.minLength,
                maxLength: field.maxLength,
              })
            : {
                value: formState[fieldName] || "",
              })}
          onChange={(e) => {
            const value = e.target.value;
            if (fieldName === "password") {
              validatePassword(value);
            }
            if (formMethods) {
              formMethods.setValue(fieldName, value, {
                shouldValidate: true,
                shouldTouch: true,
                shouldDirty: true,
              });
            } else {
              localHandleChange(value);
            }
          }}
          onBlur={() => {
            if (formMethods) {
              formMethods.trigger(fieldName);

              if (fieldName === "confirm_password") {
                const values = formMethods.getValues();
                const password = values.password || "";
                const confirmPassword = values.confirm_password || "";

                if (
                  password &&
                  confirmPassword &&
                  password !== confirmPassword
                ) {
                  setPasswordMatchError("Passwords do not match");
                } else {
                  setPasswordMatchError("");
                }
              }
            }
          }}
        />
        <img
          src={
            showPassword
              ? "/images/icons/eye_open_blue.svg"
              : "/images/icons/eye_closed_blue.svg"
          }
          alt="Toggle password visibility"
          className="password-toggle-icon"
          onClick={() => setShowPassword(!showPassword)}
        />
      </div>
      {/* Password requirements hint - only show for main password field */}
      {fieldName === "password" && (
        <div className="password-requirements">
          <div className="password-requirements-grid">
            <div
              className={`password-requirement ${
                validations.length ? "valid" : ""
              }`}
            >
              <span className="requirement-text">+8 characters</span>
              <span className="requirement-icon">
                {validations.length ? <img src="/images/icons/complete_data_circle.svg" alt="" style={{ width: "14px", height: "14px" }} /> : <img src="/images/icons/empty_data_circle.svg" alt="" style={{ width: "14px", height: "14px" }} />}
              </span>
            </div>
            <div
              className={`password-requirement ${
                validations.letter ? "valid" : ""
              }`}
            >
              <span className="requirement-text">Letter</span>
              <span className="requirement-icon">
                {validations.letter ? <img src="/images/icons/complete_data_circle.svg" alt="" style={{ width: "14px", height: "14px" }} /> : <img src="/images/icons/empty_data_circle.svg" alt="" style={{ width: "14px", height: "14px" }} />}
              </span>
            </div>
            <div
              className={`password-requirement ${
                validations.number ? "valid" : ""
              }`}
            >
              <span className="requirement-text">Number</span>
              <span className="requirement-icon">
                {validations.number ? <img src="/images/icons/complete_data_circle.svg" alt="" style={{ width: "14px", height: "14px" }} /> : <img src="/images/icons/empty_data_circle.svg" alt="" style={{ width: "14px", height: "14px" }} />}
              </span>
            </div>
            <div
              className={`password-requirement ${
                validations.special ? "valid" : ""
              }`}
            >
              <span className="requirement-text">
                Special character
              </span>
              <span className="requirement-icon">
                {validations.special ? <img src="/images/icons/complete_data_circle.svg" alt="" /> : <img src="/images/icons/empty_data_circle.svg" alt="" />}
              </span>
            </div>
          </div>
        </div>
      )}
      {/* Only show errors if "Next" has been clicked */}
      {formMethods &&
        formMethods.formState.errors[fieldName] &&
        hasAttemptedNext && (
          <ErrorLabel fieldName={field.label}>
            {formMethods.formState.errors[fieldName].message}
          </ErrorLabel>
        )}
      {/* Manual password matching error display */}
      {fieldName === "confirm_password" && formMethods && (
        <PasswordMatchError
          formMethods={formMethods}
          initialError={passwordMatchError}
          onErrorChange={handleErrorChange}
        />
      )}
    </div>
  );
};

// Rich checkbox field component that handles modal functionality
const RichCheckboxField = ({
  field,
  fieldName,
  formState,
  localHandleChange,
  formMethods,
  wrapperStyle,
  hasAttemptedNext = false,
}) => {
  const [isLegalModalOpen, setIsLegalModalOpen] = useState(false);
  const [legalUrl, setLegalUrl] = useState(null);

  const openLegalModal = (e, url) => {
    e.preventDefault();
    const withEmbed = url.includes("?") ? `${url}&embed=1` : `${url}?embed=1`;
    setLegalUrl(withEmbed);
    setIsLegalModalOpen(true);
  };

  const closeLegalModal = () => {
    setIsLegalModalOpen(false);
    setLegalUrl(null);
  };

  return (
    <div key={fieldName} className="form-field" style={wrapperStyle}>
      <div className="checkbox-container">
        <div className="checkbox-item">
          <div className="checkbox-representation">
            <input
              type="checkbox"
              id={fieldName}
              name={fieldName}
              checked={(() => {
                if (formMethods) {
                  const v = formMethods.getValues(fieldName);
                  return typeof v === "object" ? !!v?.value : !!v;
                }
                return !!formState[fieldName]?.value;
              })()}
              onChange={(e) => {
                const next = !!e.target.checked;
                if (formMethods) {
                  formMethods.setValue(
                    fieldName,
                    { value: next },
                    { shouldValidate: true, shouldDirty: true }
                  );
                } else {
                  localHandleChange(next);
                }
              }}
              {...(formMethods
                ? (() => {
                    const rp = formMethods.register(fieldName, {
                      required: field.required || false,
                    });
                    const {
                      ref: _ref,
                      onChange: _onChange,
                      ...rest
                    } = rp || {};
                    return rest;
                  })()
                : {})}
            />
            <label htmlFor={fieldName}>
              {field.rich_label.map((part, index) => {
                if (part.type === "text") {
                  return <span key={index}>{part.value}</span>;
                }
                if (part.type === "link") {
                  return (
                    <strong key={index} className="strongLnk">
                      <Link
                        to={part.href}
                        onClick={(e) => openLegalModal(e, part.href)}
                      >
                        {part.text}
                      </Link>
                    </strong>
                  );
                }
                return null;
              })}
            </label>
          </div>
        </div>
      </div>
      {/* Only show errors if "Next" has been clicked */}
      {formMethods &&
        formMethods.formState.errors[fieldName] &&
        hasAttemptedNext && (
          <ErrorLabel fieldName={field.label}>
            {formMethods.formState.errors[fieldName].message}
          </ErrorLabel>
        )}

      {isLegalModalOpen && (
        <ModalNew className="content-modal" style={{ maxWidth: "700px" }}>
          <PopupHeader
            title={
              legalUrl?.includes("terms")
                ? "Terms & Conditions"
                : legalUrl?.includes("privacy")
                ? "Privacy Policy"
                : "Service Agreement"
            }
            setOpenState={(isOpen) => {
              if (!isOpen) closeLegalModal();
            }}
            icon={`${process.env.PUBLIC_URL}/images/icons/doc_blue_popup.svg`}
            variant="compact"
          />
          <PopupContent maxHeight="70vh">
            {legalUrl && (
              <iframe
                src={legalUrl}
                title="Legal Content"
                style={{
                  width: "100%",
                  height: "60vh",
                  border: "none",
                  borderRadius: "8px",
                }}
              />
            )}
          </PopupContent>
        </ModalNew>
      )}
    </div>
  );
};

// Field name mappings for different address contexts
const getAddressFieldNames = (fieldName) => {
  // Default field names for general use
  const defaultFieldNames = {
    address: "address",
    suiteAddress: "suiteAddress",
    city: "city",
    stateAddress: "stateAddress",
    zipCode: "zipCode",
  };

  // Business address field names (for businessAddress field)
  if (fieldName === "businessAddress") {
    return {
      address: "address",
      suiteAddress: "suiteAddress",
      city: "city",
      stateAddress: "stateAddress",
      zipCode: "zipCode",
    };
  }

  // Personal address field names (for addresses field in multi_address)
  if (fieldName === "addresses") {
    return {
      address: "address",
      suiteAddress: "suiteAddress",
      city: "city",
      stateAddress: "stateAddress",
      zipCode: "zipCode",
    };
  }

  // Return default field names for other contexts
  return defaultFieldNames;
};

// Fallback label resolver for fields without explicit label
const getDefaultLabel = (name) => {
  const map = {
    firstname: "First Name",
    lastname: "Last Name",
    email: "Email",
    dateBirth: "Date of Birth",
    phoneNumber: "Phone Number",
    street: "Street Address",
    apt: "Apt, Suite, Fl.",
    city: "City",
    state: "State",
    stateAddress: "State",
    zipCode: "Zip Code",
    filingStatus: "Filing Status",
  };
  return map[name] || name;
};

// Component to display password matching errors
const PasswordMatchError = ({ formMethods, initialError, onErrorChange }) => {
  const [passwordError, setPasswordError] = useState(initialError);
  const hasShownErrorRef = useRef(!!initialError);

  useEffect(() => {
    // Si se muestra el error por primera vez (desde onBlur), marcarlo
    if (initialError && !hasShownErrorRef.current) {
      hasShownErrorRef.current = true;
      setPasswordError(initialError);
      onErrorChange(initialError);
    } else if (!initialError && hasShownErrorRef.current) {
      // Si se limpia desde afuera, también limpiar aquí
      setPasswordError("");
      onErrorChange("");
    }
  }, [initialError, onErrorChange]);

  useEffect(() => {
    // Solo escuchar cambios si ya se mostró el error alguna vez
    if (!hasShownErrorRef.current) return;

    const subscription = formMethods.watch((value) => {
      const password = value.password || "";
      const confirmPassword = value.confirm_password || "";

      if (password && confirmPassword && password === confirmPassword) {
        setPasswordError("");
        onErrorChange("");
      } else if (password && confirmPassword && password !== confirmPassword) {
        setPasswordError("Passwords do not match");
        onErrorChange("Passwords do not match");
      }
    });

    return () => subscription.unsubscribe();
  }, [formMethods, onErrorChange]);

  if (!passwordError) return null;
  return <ErrorLabel>{passwordError}</ErrorLabel>;
};

export const renderField = ({
  field,
  formState,
  setFormState,
  handleToggleComposedField,
  handleValueChange,
  formMethods, // NEW: react-hook-form methods
  hasAttemptedNext = false, // Flag to control when errors should be shown (only after "Next" clicked)
}) => {
  const fieldName = field.name;
  const wrapperStyle = {
    ...(typeof field.marginTop === "number"
      ? { marginTop: field.marginTop }
      : {}),
    ...(typeof field.marginBottom === "number"
      ? { marginBottom: field.marginBottom }
      : {}),
    // Backward compatibility with mt/mb if present
    ...(typeof field.mt === "number" ? { marginTop: field.mt } : {}),
    ...(typeof field.mb === "number" ? { marginBottom: field.mb } : {}),
  };

  const localHandleChange = (value) => {
    // Handle phone number formatting
    let processedValue = value;
    if (
      field.type === "input" &&
      field.input_type === "tel" &&
      field.name === "phone"
    ) {
      // Filter out non-digits before formatting
      const digitsOnly = value.replace(/\D/g, "");
      processedValue = formatPhoneInput(digitsOnly);
    }

    // Update old form state
    setFormState((prev) => ({
      ...prev,
      [fieldName]: processedValue,
    }));

    // Also sync to react-hook-form if available
    if (formMethods) {
      // Don't validate on change - validation only happens when clicking "Next"
      // Mark dirty so UI/Next button state updates immediately
      formMethods.setValue(fieldName, processedValue, {
        shouldValidate: false,
        shouldDirty: true,
      });
    }
  };

  switch (field.type) {
    case "section_heading":
      return (
        <div key={fieldName}>
          <h4>{field.label || field.text}</h4>
        </div>
      );
    /**
     * Custom form elements
     */
    case "single_select": {
      // Get current value for controlled select
      const selectValue = formMethods
        ? formMethods.getValues(fieldName) || ""
        : formState[fieldName] || "";

      return (
        <div key={fieldName} className="form-field">
          <FieldLabel
            label={field.label}
            description={field.description}
            htmlFor={fieldName}
            required={field.required || false}
          />
          <select
            name={fieldName}
            className="form-input"
            required={field.required || false}
            value={selectValue}
            onChange={(e) => {
              if (formMethods) {
                formMethods.setValue(fieldName, e.target.value, {
                  shouldValidate: true,
                  shouldDirty: true,
                });
              } else {
                localHandleChange(e.target.value);
              }
            }}
          >
            <option value="">{"Select an option"}</option>
            {(field.options || getSelectOptions(field.option) || []).map(
              (option, index) => (
                <option key={index} value={option.value}>
                  {option.label}
                </option>
              )
            )}
          </select>
          {/* Only show errors if "Next" has been clicked */}
          {formMethods &&
            formMethods.formState.errors[fieldName] &&
            hasAttemptedNext && (
              <ErrorLabel fieldName={field.label || getDefaultLabel(fieldName)}>
                {formMethods.formState.errors[fieldName].message}
              </ErrorLabel>
            )}
        </div>
      );
    }

    case "multi_select":
      // Special handling for incomeStates - render per-year selectors
      if (fieldName === "incomeStates" && formMethods) {
        const taxYears = formMethods.getValues("taxYears") || [];
        const incomeStates = formMethods.getValues("incomeStates") || {};

        // Ensure incomeStates is an object
        const incomeStatesObj = Array.isArray(incomeStates)
          ? {}
          : typeof incomeStates === "object" && incomeStates !== null
          ? incomeStates
          : {};

        return (
          <div key={fieldName} style={wrapperStyle} className="form-field">
            <FieldLabel
              label={field.label}
              description={field.description}
              htmlFor={fieldName}
            />
            {taxYears.length === 0 ? (
              <p
                style={{ color: "#666", fontStyle: "italic", margin: "8px 0" }}
              >
                Please select at least one tax year above to select income
                states.
              </p>
            ) : (
              <div
                style={{
                  display: "flex",
                  flexDirection: "column",
                  gap: "20px",
                }}
              >
                {taxYears.map((year) => {
                  const yearKey = String(year);
                  const yearStates = incomeStatesObj[yearKey] || [];

                  return (
                    <div
                      key={yearKey}
                      style={{
                        display: "flex",
                        flexDirection: "column",
                        gap: "8px",
                      }}
                    >
                      <label
                        style={{
                          fontSize: "14px",
                          fontWeight: "600",
                          color: "#1A2127",
                        }}
                      >
                        States for {year}:
                      </label>
                      <MultiSelect
                        options={
                          field.options || getSelectOptions(field.option) || []
                        }
                        name={`${fieldName}.${yearKey}`}
                        onChange={(value) => {
                          // Update the incomeStates object with the new value for this year
                          const updatedIncomeStates = {
                            ...incomeStatesObj,
                            [yearKey]: value || [],
                          };
                          // Remove empty arrays to keep data clean
                          if (!value || value.length === 0) {
                            delete updatedIncomeStates[yearKey];
                          }
                          localHandleChange(updatedIncomeStates);
                          // Also update react-hook-form
                          if (formMethods) {
                            formMethods.setValue(
                              fieldName,
                              updatedIncomeStates,
                              { shouldValidate: false }
                            );
                          }
                        }}
                        formMethods={formMethods}
                        fieldName={`${fieldName}.${yearKey}`}
                        placeholder={
                          field.placeholder || "Select All That Apply"
                        }
                        disabled={field.disabled || false}
                        value={yearStates}
                      />
                      {/* Only show errors if "Next" has been clicked */}
                      {formMethods &&
                        formMethods.formState.errors[
                          `${fieldName}.${yearKey}`
                        ] &&
                        hasAttemptedNext && (
                          <ErrorLabel fieldName={`${field.label} for ${year}`}>
                            {
                              formMethods.formState.errors[
                                `${fieldName}.${yearKey}`
                              ].message
                            }
                          </ErrorLabel>
                        )}
                    </div>
                  );
                })}
              </div>
            )}
            {/* Only show errors if "Next" has been clicked */}
            {formMethods &&
              formMethods.formState.errors[fieldName] &&
              hasAttemptedNext && (
                <ErrorLabel fieldName={field.label}>
                  {formMethods.formState.errors[fieldName].message}
                </ErrorLabel>
              )}
          </div>
        );
      }

      // Default multi_select rendering for other fields
      return (
        <div key={fieldName} style={wrapperStyle} className="form-field">
          <FieldLabel
            label={field.label}
            description={field.description}
            htmlFor={fieldName}
            required={field.required || false}
          />
          {/** MultiSelect works with react-hook-form via onChange pattern */}
          <MultiSelect
            options={field.options || getSelectOptions(field.option) || []}
            name={fieldName}
            onChange={(value) => {
              // value is an array of selected values
              localHandleChange(value);
            }}
            formMethods={formMethods}
            fieldName={fieldName}
            placeholder={field.placeholder || "Select All That Apply"}
            disabled={field.disabled || false}
          />
          {/* Only show errors if "Next" has been clicked */}
          {formMethods &&
            formMethods.formState.errors[fieldName] &&
            hasAttemptedNext && (
              <ErrorLabel fieldName={field.label}>
                {formMethods.formState.errors[fieldName].message}
              </ErrorLabel>
            )}
        </div>
      );

    case "composed_boolean_choice":
      return (
        <div key={fieldName} style={wrapperStyle} className="form-field">
          <ComposedInputToggle
            toggleProps={{
              type: "checkbox",
              name: fieldName,
              checked: formState[fieldName]?.status || false,
              onChange: () => handleToggleComposedField(fieldName, 0),
              ...(formMethods
                ? (() => {
                    const rp = formMethods.register(fieldName, {
                      required: field.required || false,
                    });
                    const {
                      ref: _ref,
                      onChange: _onChange,
                      ...rest
                    } = rp || {};
                    return rest;
                  })()
                : {}),
            }}
            inputProps={{
              type: "number",
              name: `numberOf_${fieldName}`,
              min: field.hide_input?.min ?? 1,
              max: field.hide_input?.max ?? 10,
              value: formState[fieldName]?.value || 0,
              onChange: (e) => {
                const nextVal = parseInt(e.target.value);
                if (Number.isNaN(nextVal)) return;
                if (formMethods) {
                  const current = formMethods.getValues(fieldName) || {};
                  formMethods.setValue(
                    fieldName,
                    {
                      status:
                        typeof current.status === "boolean"
                          ? current.status
                          : true,
                      value: nextVal,
                    },
                    { shouldValidate: false, shouldDirty: true }
                  );
                } else {
                  // Local state fallback
                  setFormState((prev) => ({
                    ...prev,
                    [fieldName]: {
                      ...(prev?.[fieldName] || {}),
                      status:
                        typeof prev?.[fieldName]?.status === "boolean"
                          ? prev[fieldName].status
                          : true,
                      value: nextVal,
                    },
                  }));
                }
              },
            }}
            label={field.label}
            labelDesc={field.hide_input?.message || ""}
            inputLabel="Number"
          />
          {/* Only show errors if "Next" has been clicked */}
          {formMethods &&
            formMethods.formState.errors[fieldName] &&
            hasAttemptedNext && (
              <ErrorLabel fieldName={field.label}>
                {formMethods.formState.errors[fieldName].message}
              </ErrorLabel>
            )}
        </div>
      );

    case "multi_boolean_choice":
      // Main checkbox state
      const isMainChecked = (() => {
        if (formMethods) {
          const v = formMethods.getValues(fieldName);
          return typeof v === "object" ? !!v?.value : !!v;
        }
        return !!formState[fieldName]?.value;
      })();

      // Handle nested fields form state - read from formMethods if available, otherwise from formState
      // Always read the latest value from formMethods on each render to ensure reactivity
      const nestedFieldsState = (() => {
        if (formMethods) {
          const v = formMethods.getValues(fieldName);
          if (v && typeof v === "object") {
            // Consolidate fields: some may be in v.fields, others directly in v (react-hook-form behavior)
            const consolidated = {};
            
            // First, get fields from v.fields if it exists
            if (v.fields && typeof v.fields === "object") {
              Object.assign(consolidated, v.fields);
            }
            
            // Then, check if there are nested fields directly in v (not value/fields properties)
            // This handles the case where react-hook-form registers nested fields at the wrong level
            Object.keys(v).forEach(key => {
              if (key !== "value" && key !== "fields" && typeof v[key] === "object" && v[key] !== null) {
                // Only add if not already in consolidated (fields takes precedence)
                if (!consolidated[key]) {
                  consolidated[key] = v[key];
                }
              }
            });
            
            return consolidated;
          }
          return {};
        }
        return formState[fieldName]?.fields || {};
      })();
      const setNestedFieldsState = (updater) => {
        if (typeof updater === "function") {
          const newNestedState = updater(nestedFieldsState);
          const newValue = {
            value: isMainChecked,
            fields: newNestedState,
          };
          if (formMethods) {
            formMethods.setValue(fieldName, newValue, {
              shouldValidate: false,
              shouldDirty: true,
            });
          }
          setFormState((prev) => ({
            ...prev,
            [fieldName]: newValue,
          }));
        } else {
          const newValue = {
            value: isMainChecked,
            fields: updater,
          };
          if (formMethods) {
            formMethods.setValue(fieldName, newValue, {
              shouldValidate: false,
              shouldDirty: true,
            });
          }
          setFormState((prev) => ({
            ...prev,
            [fieldName]: newValue,
          }));
        }
      };

      return (
        <div key={fieldName} className="form-field" style={wrapperStyle}>
          <div className="checkbox-container">
            <div className="checkbox-item">
              <div className="checkbox-representation">
                <input
                  type="checkbox"
                  id={fieldName}
                  name={fieldName}
                  checked={isMainChecked}
                  onChange={(e) => {
                    const next = !!e.target.checked;
                    let initialFields = {};

                    // Helper to recursively clear nested fields
                    const clearNestedFields = (nestedField) => {
                      if (nestedField.type === "multi_boolean_choice") {
                        return { value: false, fields: {} };
                      } else if (nestedField.type === "radio_boolean_choice") {
                        return { value: "", fields: {} };
                      } else if (nestedField.type === "single_boolean_choice") {
                        return { value: false };
                      } else if (nestedField.type === "numeric") {
                        return nestedField.inputType === "integer" ? 0 : "";
                      } else if (nestedField.type === "multi_select") {
                        return [];
                      }
                      return null;
                    };

                    // If checkbox is unchecked, clear nested fields and clear any errors
                    if (!next && formMethods) {
                      // Clear nested field errors when parent checkbox is unchecked
                      field.fields?.forEach((nestedField) => {
                        const nestedFieldName = `${fieldName}.fields.${nestedField.name}`;
                        formMethods.clearErrors(nestedFieldName);
                      });
                    }

                    // Initialize nested fields based on checkbox state
                    if (field.fields && Array.isArray(field.fields)) {
                      field.fields.forEach((nestedField) => {
                        if (!next) {
                          // When unchecked, clear all nested fields recursively
                          const cleared = clearNestedFields(nestedField);
                          if (cleared !== null) {
                            initialFields[nestedField.name] = cleared;
                          }
                        } else {
                          // When checked, initialize with defaults if not already present
                          if (
                            nestedField.type === "numeric" &&
                            nestedField.inputType === "integer"
                          ) {
                            // Integer fields: use defaultValue if specified, otherwise 0
                            if (!nestedFieldsState[nestedField.name]) {
                              initialFields[nestedField.name] = nestedField.defaultValue !== undefined ? nestedField.defaultValue : 0;
                            }
                          } else if (
                            nestedField.type === "multi_boolean_choice"
                          ) {
                            // Multi boolean choice: preserve existing or initialize
                            if (!nestedFieldsState[nestedField.name]) {
                              initialFields[nestedField.name] = {
                                value: false,
                                fields: {},
                              };
                            }
                          } else if (
                            nestedField.type === "radio_boolean_choice"
                          ) {
                            // Radio boolean choice: preserve existing or initialize
                            if (!nestedFieldsState[nestedField.name]) {
                              initialFields[nestedField.name] = {
                                value: "",
                                fields: {},
                              };
                            }
                          }
                        }
                      });
                    }

                    const newValue = {
                      value: next,
                      fields: next
                        ? { ...nestedFieldsState, ...initialFields }
                        : {}, // Clear all fields when unchecked
                    };

                    if (formMethods) {
                      formMethods.setValue(fieldName, newValue, {
                        shouldValidate: false,
                        shouldDirty: true,
                      });

                      // If checkbox is unchecked, clear all nested field errors
                      if (!next) {
                        // Clear all nested field errors when parent checkbox is unchecked
                        field.fields?.forEach((nestedField) => {
                          // Try different possible error paths
                          const paths = [
                            `${fieldName}.fields.${nestedField.name}`,
                            `${fieldName}.${nestedField.name}`,
                            `${fieldName}.fields`,
                            fieldName,
                          ];
                          paths.forEach((path) => {
                            formMethods.clearErrors(path);
                          });
                        });
                        // Also clear the parent field error
                        formMethods.clearErrors(fieldName);

                        // Clear any flattened nested fields
                        const allFormValues = formMethods.getValues();
                        if (
                          allFormValues &&
                          typeof allFormValues === "object"
                        ) {
                          Object.keys(allFormValues).forEach((key) => {
                            if (key.startsWith(`${fieldName}.`)) {
                              formMethods.setValue(key, undefined, {
                                shouldValidate: false,
                              });
                              formMethods.clearErrors(key);
                            }
                          });
                        }
                      }
                    } else {
                      setFormState((prev) => ({
                        ...prev,
                        [fieldName]: newValue,
                      }));
                    }
                  }}
                  {...(formMethods
                    ? (() => {
                        const rp = formMethods.register(fieldName, {
                          required: field.required || false,
                        });
                        const {
                          ref: _ref,
                          onChange: _onChange,
                          ...rest
                        } = rp || {};
                        return rest;
                      })()
                    : {})}
                />
                <FieldLabel
                  label={field.label}
                  description={field.description}
                  htmlFor={fieldName}
                />
              </div>
            </div>
          </div>

          {/* Render nested fields when main checkbox is checked */}
          {isMainChecked && field.fields && Array.isArray(field.fields) && (
            <div className="nested-fields-container">
              {field.fields.map((nestedField, index) => {
                const nestedFieldKey = nestedField.name || `field_${index}`;
                const nestedFieldName = `${fieldName}.fields.${nestedFieldKey}`;

                // Check if field should be conditionally displayed
                const shouldDisplay = (() => {
                  if (!nestedField.conditionalDisplay) return true;
                  
                  const { field: targetField, operator, value: targetValue } = nestedField.conditionalDisplay;
                  
                  // Get the value of the field we're checking against
                  const currentFieldValue = nestedFieldsState[targetField];
                  
                  // Evaluate based on operator
                  switch (operator) {
                    case "includes":
                      // For arrays, check if it includes the target value
                      return Array.isArray(currentFieldValue) && currentFieldValue.includes(targetValue);
                    case "equals":
                      return currentFieldValue === targetValue;
                    case "notEquals":
                      return currentFieldValue !== targetValue;
                    default:
                      return true;
                  }
                })();

                // If field should not be displayed, return null
                if (!shouldDisplay) return null;

                // Create handlers for nested fields
                const nestedHandleValueChange = (name, value) => {
                  // Update nested fields state and parent field in formMethods
                  setNestedFieldsState((prev) => {
                    const updated = {
                      ...prev,
                      [name]: value,
                    };

                    // Debug logging for llc_owner
                    if (fieldName === "llc_owner") {
                      console.log(
                        "[nestedHandleValueChange] llc_owner - name:",
                        name,
                        "value:",
                        JSON.stringify(value, null, 2)
                      );
                      console.log(
                        "[nestedHandleValueChange] llc_owner - prev:",
                        JSON.stringify(prev, null, 2)
                      );
                      console.log(
                        "[nestedHandleValueChange] llc_owner - updated:",
                        JSON.stringify(updated, null, 2)
                      );
                    }

                    // Update the parent field in formMethods to persist the change
                    if (formMethods) {
                      const currentParentValue = formMethods.getValues(
                        fieldName
                      ) || { value: isMainChecked, fields: {} };
                      const newParentValue = {
                        value:
                          currentParentValue.value !== undefined
                            ? currentParentValue.value
                            : isMainChecked,
                        fields: updated, // Use the updated nested fields state
                      };

                      if (fieldName === "llc_owner") {
                        console.log(
                          "[nestedHandleValueChange] llc_owner - setting parent value:",
                          JSON.stringify(newParentValue, null, 2)
                        );
                      }

                      formMethods.setValue(fieldName, newParentValue, {
                        shouldValidate: false,
                        shouldDirty: true,
                      });

                      // Don't trigger validation here - validation only happens when "Next" is clicked
                      // Errors will be cleared automatically when user clicks "Next" again and validation passes
                    }

                    return updated;
                  });
                };

                const nestedSetFormState = (updater) => {
                  if (typeof updater === "function") {
                    setNestedFieldsState(updater);
                  } else {
                    setNestedFieldsState(updater);
                  }
                };

                // Render nested field based on its type
                if (nestedField.type === "numeric") {
                  const isInteger = nestedField.inputType === "integer";
                  const storedValue = nestedFieldsState[nestedFieldKey];
                  let displayValue = "";
                  const numericMinValue =
                    nestedField.min !== undefined
                      ? nestedField.min
                      : isInteger
                      ? 1
                      : undefined;

                  if (storedValue === null || storedValue === undefined) {
                    // Default integer fields: use defaultValue from schema if specified, otherwise 0
                    // Currency fields default to empty
                    // Note: If checkbox is unchecked, value should be 0 (handled in checkbox onChange)
                    displayValue = isInteger ? String(nestedField.defaultValue !== undefined ? nestedField.defaultValue : 0) : "";
                  } else if (storedValue === "") {
                    // Allow empty string while user is typing (can be cleared)
                    displayValue = "";
                  } else if (typeof storedValue === "number") {
                    // For integer fields, if value is 0 and checkbox is checked, show 0
                    // (0 is the default value)
                    if (isInteger && storedValue === 0 && isMainChecked) {
                      displayValue = "0";
                    } else {
                      displayValue = isInteger
                        ? Math.floor(storedValue).toString()
                        : storedValue.toFixed(2);
                    }
                  } else {
                    // If it's a string, use it directly (while typing)
                    displayValue = storedValue;
                  }

                  // Currency input rendering
                  if (!isInteger) {
                    return (
                      <div
                        key={nestedFieldName}
                        className="form-field"
                      >
                        <div className="numeric-input-container">
                          <div className="currency-input-wrapper">
                            <span className="currency-symbol">
                              $
                            </span>
                            <input
                              type="text"
                              id={nestedFieldName}
                              name={nestedFieldName}
                              className="form-input"
                              value={displayValue}
                              onChange={(e) => {
                                const inputValue = e.target.value;
                                // Remove all non-numeric characters except decimal point
                                const cleaned = inputValue.replace(
                                  /[^\d.]/g,
                                  ""
                                );
                                // Allow only one decimal point
                                const parts = cleaned.split(".");
                                let formatted =
                                  parts.length > 2
                                    ? parts[0] + "." + parts.slice(1).join("")
                                    : cleaned;

                                // Limit to 2 decimal places
                                if (formatted.includes(".")) {
                                  const [integer, decimal] =
                                    formatted.split(".");
                                  formatted =
                                    integer +
                                    "." +
                                    (decimal ? decimal.slice(0, 2) : "");
                                }

                                // Store as string while typing
                                nestedHandleValueChange(
                                  nestedFieldKey,
                                  formatted
                                );

                                // Also update react-hook-form if available
                                if (formMethods) {
                                  formMethods.setValue(
                                    nestedFieldName,
                                    formatted,
                                    {
                                      shouldValidate: false,
                                      shouldDirty: true,
                                    }
                                  );
                                }
                              }}
                              onBlur={(e) => {
                                // Format to 2 decimal places and convert to number on blur
                                const value = e.target.value.trim();
                                let finalValue = "";

                                if (value === "") {
                                  finalValue = "";
                                } else if (
                                  !isNaN(parseFloat(value)) &&
                                  parseFloat(value) >= 0
                                ) {
                                  const numValue = parseFloat(value);
                                  const formatted = numValue.toFixed(2);
                                  // Store as number after formatting
                                  finalValue = parseFloat(formatted);
                                } else {
                                  // Invalid value, clear it
                                  finalValue = "";
                                }

                                nestedHandleValueChange(
                                  nestedFieldKey,
                                  finalValue
                                );

                                // Update react-hook-form with the final value
                                if (formMethods) {
                                  formMethods.setValue(
                                    nestedFieldName,
                                    finalValue,
                                    {
                                      shouldValidate: false,
                                      shouldDirty: true,
                                    }
                                  );
                                }
                              }}
                              placeholder="0.00"
                              {...(formMethods
                                ? (() => {
                                    const registerResult = formMethods.register(
                                      nestedFieldName,
                                      {
                                        required: nestedField.required || false,
                                        validate: (value) => {
                                          // Get the actual value from form state
                                          const actualValue =
                                            nestedFieldsState[nestedFieldKey];
                                          if (
                                            nestedField.required &&
                                            (!actualValue || actualValue === "")
                                          ) {
                                            return `${nestedField.label} is required`;
                                          }
                                          const numValue =
                                            typeof actualValue === "number"
                                              ? actualValue
                                              : parseFloat(actualValue);
                                          if (
                                            actualValue &&
                                            (isNaN(numValue) || numValue < 0)
                                          ) {
                                            return `${nestedField.label} must be a valid positive number`;
                                          }
                                          return true;
                                        },
                                      }
                                    );

                                    // Return only name and ref, we handle onChange/onBlur ourselves
                                    const {
                                      onChange: _onChange,
                                      onBlur: _onBlur,
                                      ...rest
                                    } = registerResult;
                                    return rest;
                                  })()
                                : {})}
                            />
                          </div>
                          <FieldLabel
                            label={nestedField.label}
                            description={nestedField.description}
                            htmlFor={nestedFieldName}
                            className=""
                            required={nestedField.required || false}
                          />
                        </div>
                        {formMethods &&
                          formMethods.formState.errors[nestedFieldName] && (
                            <ErrorLabel fieldName={nestedField.label}>
                              {
                                formMethods.formState.errors[nestedFieldName]
                                  .message
                              }
                            </ErrorLabel>
                          )}
                      </div>
                    );
                  }

                  // Integer input rendering (no currency formatting)
                  const minValue =
                    nestedField.min !== undefined ? nestedField.min : 1;
                  const maxValue =
                    nestedField.max !== undefined ? nestedField.max : undefined;

                  return (
                    <div
                      key={nestedFieldName}
                      className="form-field"
                    >
                      <div className="numeric-input-container">
                        <div className="integer-input-wrapper">
                          <input
                            type="number"
                            id={nestedFieldName}
                            name={nestedFieldName}
                            className="form-input"
                            min={minValue}
                            max={maxValue}
                            step="1"
                            value={displayValue}
                            onChange={(e) => {
                              const inputValue = e.target.value;

                              // Allow empty string while user is typing/clearing
                              if (inputValue === "") {
                                nestedHandleValueChange(nestedFieldKey, "");
                                if (formMethods) {
                                  formMethods.setValue(nestedFieldName, "", {
                                    shouldValidate: false,
                                    shouldDirty: true,
                                  });
                                }
                                return;
                              }

                              // Only allow positive integers
                              const cleaned = inputValue.replace(/[^\d]/g, "");
                              let numValue;

                              if (cleaned === "") {
                                // Allow empty while typing, will default to minValue on blur
                                numValue = "";
                              } else {
                                const parsed = parseInt(cleaned, 10);
                                // Only enforce minimum if user has entered a valid number
                                // Don't force to minValue while they're still typing
                                if (isNaN(parsed)) {
                                  numValue = "";
                                } else if (
                                  maxValue !== undefined &&
                                  parsed > maxValue
                                ) {
                                  numValue = maxValue;
                                } else if (parsed < minValue) {
                                  numValue = "";
                                } else {
                                  numValue = parsed;
                                }
                              }

                              // Store as number (or empty string while typing)
                              nestedHandleValueChange(nestedFieldKey, numValue);

                              // Also update react-hook-form if available
                              if (formMethods) {
                                formMethods.setValue(
                                  nestedFieldName,
                                  numValue,
                                  {
                                    shouldValidate: false,
                                    shouldDirty: true,
                                  }
                                );
                              }
                            }}
                            onBlur={(e) => {
                              const value = e.target.value.trim();
                              let finalValue = 0; // Default to 0 for integer fields

                              if (value === "") {
                                finalValue = 0; // Default to 0 if empty
                              } else if (!isNaN(parseInt(value, 10))) {
                                finalValue = parseInt(value, 10);
                                // Ensure value is within min/max range
                                if (finalValue < minValue) {
                                  finalValue = minValue;
                                } else if (
                                  maxValue !== undefined &&
                                  finalValue > maxValue
                                ) {
                                  finalValue = maxValue;
                                }
                              } else {
                                finalValue = 0; // Default to 0 if invalid
                              }

                              nestedHandleValueChange(
                                nestedFieldKey,
                                finalValue
                              );

                              // Update react-hook-form with the final value
                              if (formMethods) {
                                formMethods.setValue(
                                  nestedFieldName,
                                  finalValue,
                                  {
                                    shouldValidate: false,
                                    shouldDirty: true,
                                  }
                                );
                              }
                            }}
                            placeholder="0"
                            {...(formMethods
                              ? (() => {
                                  const registerResult = formMethods.register(
                                    nestedFieldName,
                                    {
                                      required: nestedField.required || false,
                                      validate: (value) => {
                                        // Get the actual value from form state
                                        const actualValue =
                                          nestedFieldsState[nestedFieldKey];
                                        if (
                                          nestedField.required &&
                                          (!actualValue || actualValue === "")
                                        ) {
                                          return `${nestedField.label} is required`;
                                        }
                                        const numValue =
                                          typeof actualValue === "number"
                                            ? actualValue
                                            : parseInt(actualValue, 10);
                                        if (
                                          actualValue &&
                                          !isNaN(numValue) &&
                                          Number.isInteger(numValue)
                                        ) {
                                          if (numValue < minValue) {
                                            return `${nestedField.label} must be at least ${minValue}`;
                                          }
                                          if (
                                            maxValue !== undefined &&
                                            numValue > maxValue
                                          ) {
                                            return `${nestedField.label} must be at most ${maxValue}`;
                                          }
                                        } else if (
                                          actualValue &&
                                          (isNaN(numValue) ||
                                            !Number.isInteger(numValue))
                                        ) {
                                          return `${nestedField.label} must be a valid integer`;
                                        }
                                        return true;
                                      },
                                    }
                                  );

                                  // Return only name and ref, we handle onChange/onBlur ourselves
                                  const {
                                    onChange: _onChange,
                                    onBlur: _onBlur,
                                    ...rest
                                  } = registerResult;
                                  return rest;
                                })()
                              : {})}
                          />
                        </div>
                        <FieldLabel
                          label={nestedField.label}
                          description={nestedField.description}
                          htmlFor={nestedFieldName}
                          className=""
                        />
                      </div>
                      {/* Only show errors if "Next" has been clicked */}
                      {formMethods &&
                        formMethods.formState.errors[nestedFieldName] &&
                        hasAttemptedNext && (
                          <ErrorLabel fieldName={nestedField.label}>
                            {
                              formMethods.formState.errors[nestedFieldName]
                                .message
                            }
                          </ErrorLabel>
                        )}
                    </div>
                  );
                } else if (nestedField.type === "multi_property") {
                  // Get the count from rentalPropertiesCount - read from formMethods if available for latest value
                  let propertiesCount = 0;
                  let propertiesArray = [];

                  if (formMethods) {
                    const parentValue = formMethods.getValues(fieldName);
                    if (
                      parentValue &&
                      typeof parentValue === "object" &&
                      parentValue.fields
                    ) {
                      propertiesCount =
                        parentValue.fields["rentalPropertiesCount"] || 0;
                      propertiesArray =
                        parentValue.fields[nestedFieldKey] || [];
                    }
                  } else {
                    propertiesCount =
                      nestedFieldsState["rentalPropertiesCount"] || 0;
                    propertiesArray = nestedFieldsState[nestedFieldKey] || [];
                  }

                  // Ensure properties array matches the count
                  const currentCount = Array.isArray(propertiesArray)
                    ? propertiesArray.length
                    : 0;
                  if (currentCount !== propertiesCount && propertiesCount > 0) {
                    // Initialize or adjust the array
                    const newPropertiesArray = Array.from(
                      { length: propertiesCount },
                      (_, index) => {
                        return (
                          propertiesArray[index] || {
                            isResidential: false,
                            isShortTermRental: false,
                            isNewlyPurchased: false,
                          }
                        );
                      }
                    );
                    nestedHandleValueChange(nestedFieldKey, newPropertiesArray);
                    // Update the array reference for rendering
                    propertiesArray = newPropertiesArray;
                  }

                  return (
                    <div
                      key={nestedFieldName}
                      className="form-field multi-property-field"
                    >
                      {nestedField.label && (
                        <FieldLabel
                          label={nestedField.label}
                          description={nestedField.description}
                          htmlFor={nestedFieldName}
                          required={nestedField.required || false}
                        />
                      )}
                      {propertiesCount > 0 ? (
                        <div className="multi-property-container">
                          {Array.from(
                            { length: propertiesCount },
                            (_, index) => {
                              const propertyIndex = index;
                              // Always read fresh from state
                              let property = propertiesArray[propertyIndex];
                              if (!property) {
                                property = {
                                  isResidential: false,
                                  isShortTermRental: false,
                                  isNewlyPurchased: false,
                                };
                              }

                              return (
                                <div
                                  key={`property-${propertyIndex}`}
                                  className="property-card"
                                >
                                  <strong className="property-card-title">
                                    Property {propertyIndex + 1}:
                                  </strong>
                                  <div className="property-card-fields">
                                    {(() => {
                                      // Get propertyFields from nestedField, with fallback to default fields
                                      let propertyFields =
                                        nestedField.propertyFields || [];

                                      // Fallback to default property fields if not provided
                                      if (
                                        !Array.isArray(propertyFields) ||
                                        propertyFields.length === 0
                                      ) {
                                        propertyFields = [
                                          {
                                            name: "isResidential",
                                            label: "Is Residential?",
                                            type: "boolean",
                                          },
                                          {
                                            name: "isShortTermRental",
                                            label:
                                              "Is Short-Term Rent (Airbnb)?",
                                            type: "boolean",
                                          },
                                          {
                                            name: "isNewlyPurchased",
                                            label: "Is Newly Purchased?",
                                            type: "boolean",
                                          },
                                        ];
                                      }

                                      return propertyFields.map((propField) => {
                                        const propFieldName = `${nestedFieldName}.${propertyIndex}.${propField.name}`;

                                        // Always read the latest value from formMethods or state
                                        const getCurrentValue = () => {
                                          if (formMethods) {
                                            const parentValue =
                                              formMethods.getValues(fieldName);
                                            if (
                                              parentValue &&
                                              typeof parentValue === "object" &&
                                              parentValue.fields
                                            ) {
                                              const currentProperties =
                                                parentValue.fields[
                                                  nestedFieldKey
                                                ] || [];
                                              const currentProperty =
                                                currentProperties[
                                                  propertyIndex
                                                ];
                                              return currentProperty
                                                ? currentProperty[
                                                    propField.name
                                                  ] || false
                                                : false;
                                            }
                                          }
                                          // Fallback to nestedFieldsState
                                          const currentProperties =
                                            nestedFieldsState[nestedFieldKey] ||
                                            [];
                                          const currentProperty =
                                            currentProperties[propertyIndex];
                                          return currentProperty
                                            ? currentProperty[propField.name] ||
                                                false
                                            : false;
                                        };

                                        const propValue = getCurrentValue();

                                        return (
                                          <div
                                            key={propFieldName}
                                            className="checkbox-container"
                                          >
                                            <div className="checkbox-item">
                                              <div className="checkbox-representation">
                                                <input
                                                  type="checkbox"
                                                  id={propFieldName}
                                                  name={propFieldName}
                                                  checked={propValue}
                                                  onChange={(e) => {
                                                    const newCheckedValue =
                                                      e.target.checked;

                                                    // Get fresh state
                                                    let currentProperties = [];
                                                    if (formMethods) {
                                                      const parentValue =
                                                        formMethods.getValues(
                                                          fieldName
                                                        );
                                                      if (
                                                        parentValue &&
                                                        typeof parentValue ===
                                                          "object" &&
                                                        parentValue.fields
                                                      ) {
                                                        currentProperties =
                                                          parentValue.fields[
                                                            nestedFieldKey
                                                          ] || [];
                                                      }
                                                    } else {
                                                      currentProperties =
                                                        nestedFieldsState[
                                                          nestedFieldKey
                                                        ] || [];
                                                    }

                                                    // Create new array with updated property
                                                    const newProperties = [
                                                      ...currentProperties,
                                                    ];
                                                    if (
                                                      !newProperties[
                                                        propertyIndex
                                                      ]
                                                    ) {
                                                      newProperties[
                                                        propertyIndex
                                                      ] = {
                                                        isResidential: false,
                                                        isShortTermRental: false,
                                                        isNewlyPurchased: false,
                                                      };
                                                    }
                                                    newProperties[
                                                      propertyIndex
                                                    ] = {
                                                      ...newProperties[
                                                        propertyIndex
                                                      ],
                                                      [propField.name]:
                                                        newCheckedValue,
                                                    };

                                                    // Update react-hook-form first (this will trigger re-render)
                                                    if (formMethods) {
                                                      const currentParentValue =
                                                        formMethods.getValues(
                                                          fieldName
                                                        ) || {
                                                          value: isMainChecked,
                                                          fields: {},
                                                        };
                                                      const updatedFields = {
                                                        ...currentParentValue.fields,
                                                        [nestedFieldKey]:
                                                          newProperties,
                                                      };
                                                      formMethods.setValue(
                                                        fieldName,
                                                        {
                                                          value:
                                                            currentParentValue.value !==
                                                            undefined
                                                              ? currentParentValue.value
                                                              : isMainChecked,
                                                          fields: updatedFields,
                                                        },
                                                        {
                                                          shouldValidate: false,
                                                          shouldDirty: true,
                                                        }
                                                      );
                                                    }

                                                    // Then update local state
                                                    nestedHandleValueChange(
                                                      nestedFieldKey,
                                                      newProperties
                                                    );
                                                  }}
                                                />
                                                <label htmlFor={propFieldName}>
                                                  {propField.label}
                                                </label>
                                              </div>
                                            </div>
                                          </div>
                                        );
                                      });
                                    })()}
                                  </div>
                                </div>
                              );
                            }
                          )}
                        </div>
                      ) : (
                        <div
                          style={{
                            color: "#666",
                            fontStyle: "italic",
                            fontSize: "14px",
                            marginTop: "8px",
                          }}
                        >
                          Please enter the number of properties above.
                        </div>
                      )}
                    </div>
                  );
                } else if (nestedField.type === "single_boolean_choice") {
                  // Get the nested field value from nestedFieldsState or formMethods
                  const nestedFieldValue = (() => {
                    if (formMethods) {
                      const parentValue = formMethods.getValues(fieldName);
                      if (
                        parentValue &&
                        typeof parentValue === "object" &&
                        parentValue.fields
                      ) {
                        return parentValue.fields[nestedFieldKey] || { value: false };
                      }
                    }
                    return nestedFieldsState[nestedFieldKey] || { value: false };
                  })();

                  // Create a form state structure that single_boolean_choice expects
                  // single_boolean_choice expects formState[fieldName]?.value
                  const nestedFormStateForCheckbox = {
                    [nestedFieldName]: nestedFieldValue,
                  };

                  // Recursively render single_boolean_choice
                  return (
                    <div key={nestedFieldName} className="nested-field-wrapper">
                      {renderField({
                        field: {
                          ...nestedField,
                          name: nestedFieldName,
                        },
                        formState: nestedFormStateForCheckbox,
                        setFormState: (updater) => {
                          if (typeof updater === "function") {
                            const newState = updater(
                              nestedFormStateForCheckbox
                            );
                            const newValue = newState[nestedFieldName];
                            nestedHandleValueChange(nestedFieldKey, newValue);
                          } else {
                            const newValue = updater[nestedFieldName];
                            nestedHandleValueChange(nestedFieldKey, newValue);
                          }
                        },
                        handleToggleComposedField: () => {},
                        handleValueChange: (name, value) => {
                          nestedHandleValueChange(nestedFieldKey, { value });
                        },
                        formMethods,
                      })}
                    </div>
                  );
                } else if (nestedField.type === "multi_boolean_choice") {
                  // Get the nested field value from nestedFieldsState or formMethods
                  const nestedFieldValue = (() => {
                    if (formMethods) {
                      const parentValue = formMethods.getValues(fieldName);
                      if (
                        parentValue &&
                        typeof parentValue === "object" &&
                        parentValue.fields
                      ) {
                        return (
                          parentValue.fields[nestedFieldKey] || {
                            value: false,
                            fields: {},
                          }
                        );
                      }
                    }
                    return (
                      nestedFieldsState[nestedFieldKey] || {
                        value: false,
                        fields: {},
                      }
                    );
                  })();

                  // Create a custom formMethods-like object that reads/writes to the nested field
                  // This allows the nested multi_boolean_choice to work as if it's a top-level field
                  const nestedFormMethods = formMethods
                    ? {
                        getValues: (name) => {
                          // When reading, get from the nested field structure
                          if (name === nestedFieldName) {
                            return nestedFieldValue;
                          }
                          // For nested fields within this nested multi_boolean_choice, read from nestedFieldValue.fields
                          if (
                            nestedFieldValue &&
                            typeof nestedFieldValue === "object" &&
                            nestedFieldValue.fields
                          ) {
                            return nestedFieldValue.fields[name];
                          }
                          return undefined;
                        },
                        setValue: (name, value, options) => {
                          // When setting, update the nested field in the parent
                          if (name === nestedFieldName) {
                            nestedHandleValueChange(nestedFieldKey, value);
                          } else {
                            // For nested fields within this nested multi_boolean_choice, update nestedFieldValue.fields
                            const updatedNestedValue = {
                              ...nestedFieldValue,
                              fields: {
                                ...(nestedFieldValue?.fields || {}),
                                [name]: value,
                              },
                            };
                            nestedHandleValueChange(
                              nestedFieldKey,
                              updatedNestedValue
                            );
                          }
                        },
                        watch: (name) => {
                          // Watch the nested field
                          if (name === nestedFieldName) {
                            return nestedFieldValue;
                          }
                          if (
                            nestedFieldValue &&
                            typeof nestedFieldValue === "object" &&
                            nestedFieldValue.fields
                          ) {
                            return nestedFieldValue.fields[name];
                          }
                          return undefined;
                        },
                        register: (name, options) => {
                          // For nested fields, we don't need to register with react-hook-form
                          // since they're part of the parent field's structure
                          // Return a minimal object that satisfies the register API
                          return {
                            name: name,
                            onChange: () => {},
                            onBlur: () => {},
                            ref: () => {},
                          };
                        },
                        formState: formMethods.formState || { errors: {} },
                        trigger: () => Promise.resolve(true),
                        reset: () => {},
                        resetField: () => {},
                        clearErrors: (name) => {
                          // Clear errors using the actual parent formMethods with the correct path
                          if (name === nestedFieldName) {
                            // Clear the nested field error
                            const nestedErrorPath = `${fieldName}.fields.${nestedFieldKey}`;
                            formMethods.clearErrors(nestedErrorPath);
                            // Also clear parent field error if it exists
                            formMethods.clearErrors(fieldName);
                          } else {
                            // Clear nested field errors within this nested multi_boolean_choice
                            const nestedErrorPath = `${fieldName}.fields.${nestedFieldKey}.fields.${name}`;
                            formMethods.clearErrors(nestedErrorPath);
                          }
                        },
                      }
                    : null;

                  // Create a form state structure that multi_boolean_choice expects
                  // multi_boolean_choice expects formState[fieldName]?.value and formState[fieldName]?.fields
                  const nestedFormStateForMultiChoice = {
                    [nestedFieldName]: nestedFieldValue,
                  };

                  // Recursively render multi_boolean_choice
                  // When a nested multi_boolean_choice updates, we need to ensure it preserves its full structure
                  return (
                    <div key={nestedFieldName} className="nested-field-wrapper">
                      {renderField({
                        field: {
                          ...nestedField,
                          name: nestedFieldName,
                        },
                        formState: nestedFormStateForMultiChoice,
                        setFormState: (updater) => {
                          if (typeof updater === "function") {
                            const newState = updater(
                              nestedFormStateForMultiChoice
                            );
                            const newValue = newState[nestedFieldName];
                            nestedHandleValueChange(nestedFieldKey, newValue);
                          } else {
                            const newValue = updater[nestedFieldName];
                            nestedHandleValueChange(nestedFieldKey, newValue);
                          }
                        },
                        handleToggleComposedField: () => {},
                        handleValueChange: (name, value) => {
                          nestedHandleValueChange(nestedFieldKey, value);
                        },
                        formMethods: nestedFormMethods,
                      })}
                    </div>
                  );
                } else if (nestedField.type === "radio_boolean_choice") {
                  // Get the nested field value from nestedFieldsState or formMethods
                  const nestedFieldValue = (() => {
                    if (formMethods) {
                      const parentValue = formMethods.getValues(fieldName);
                      if (
                        parentValue &&
                        typeof parentValue === "object" &&
                        parentValue.fields
                      ) {
                        return (
                          parentValue.fields[nestedFieldKey] || {
                            value: "",
                            fields: {},
                          }
                        );
                      }
                    }
                    return (
                      nestedFieldsState[nestedFieldKey] || {
                        value: "",
                        fields: {},
                      }
                    );
                  })();

                  // Create a custom formMethods for nested radio_boolean_choice
                  // This ensures it reads/writes from the nested structure, not flattened paths
                  const nestedFormMethods = formMethods
                    ? {
                        ...formMethods,
                        getValues: (path) => {
                          // Strip any path prefix that matches the nested field name
                          let simplePath = path;
                          if (path.startsWith(`${nestedFieldKey}.`)) {
                            simplePath = path.substring(
                              nestedFieldKey.length + 1
                            );
                          } else if (path.startsWith(`${nestedFieldName}.`)) {
                            simplePath = path.substring(
                              nestedFieldName.length + 1
                            );
                          }

                          if (
                            path === nestedFieldKey ||
                            path === nestedFieldName ||
                            simplePath === nestedFieldKey
                          ) {
                            return nestedFieldValue;
                          }
                          // For nested fields within the radio, read from nestedFieldValue.fields using simple path
                          if (
                            nestedFieldValue &&
                            nestedFieldValue.fields &&
                            nestedFieldValue.fields[simplePath]
                          ) {
                            return nestedFieldValue.fields[simplePath];
                          }
                          return formMethods.getValues(path);
                        },
                        setValue: (path, value, options) => {
                          // Strip any path prefix that matches the nested field name
                          let simplePath = path;
                          if (path.startsWith(`${nestedFieldKey}.`)) {
                            simplePath = path.substring(
                              nestedFieldKey.length + 1
                            );
                          } else if (path.startsWith(`${nestedFieldName}.`)) {
                            simplePath = path.substring(
                              nestedFieldName.length + 1
                            );
                          }

                          if (
                            path === nestedFieldKey ||
                            path === nestedFieldName ||
                            simplePath === nestedFieldKey
                          ) {
                            // Update the nested field in the parent
                            nestedHandleValueChange(nestedFieldKey, value);
                          } else {
                            // For nested fields within the radio, update nestedFieldValue.fields using simple path
                            const updatedNestedValue = {
                              ...nestedFieldValue,
                              fields: {
                                ...(nestedFieldValue.fields || {}),
                                [simplePath]: value,
                              },
                            };
                            nestedHandleValueChange(
                              nestedFieldKey,
                              updatedNestedValue
                            );
                          }
                        },
                        watch: (path) => {
                          // Strip any path prefix that matches the nested field name
                          let simplePath = path;
                          if (path.startsWith(`${nestedFieldKey}.`)) {
                            simplePath = path.substring(
                              nestedFieldKey.length + 1
                            );
                          } else if (path.startsWith(`${nestedFieldName}.`)) {
                            simplePath = path.substring(
                              nestedFieldName.length + 1
                            );
                          }

                          if (
                            path === nestedFieldKey ||
                            path === nestedFieldName ||
                            simplePath === nestedFieldKey
                          ) {
                            return nestedFieldValue;
                          }
                          if (
                            nestedFieldValue &&
                            nestedFieldValue.fields &&
                            nestedFieldValue.fields[simplePath]
                          ) {
                            return nestedFieldValue.fields[simplePath];
                          }
                          return formMethods.watch(path);
                        },
                        register: (path) => ({
                          name: path,
                          onChange: (e) => {
                            const value =
                              e.target.type === "checkbox"
                                ? e.target.checked
                                : e.target.value;

                            // Strip any path prefix that matches the nested field name
                            let simplePath = path;
                            if (path.startsWith(`${nestedFieldKey}.`)) {
                              simplePath = path.substring(
                                nestedFieldKey.length + 1
                              );
                            } else if (path.startsWith(`${nestedFieldName}.`)) {
                              simplePath = path.substring(
                                nestedFieldName.length + 1
                              );
                            }

                            if (
                              path === nestedFieldKey ||
                              path === nestedFieldName ||
                              simplePath === nestedFieldKey
                            ) {
                              nestedHandleValueChange(nestedFieldKey, {
                                value,
                                fields: {},
                              });
                            } else {
                              const updatedNestedValue = {
                                ...nestedFieldValue,
                                fields: {
                                  ...(nestedFieldValue.fields || {}),
                                  [simplePath]: { value },
                                },
                              };
                              nestedHandleValueChange(
                                nestedFieldKey,
                                updatedNestedValue
                              );
                            }
                          },
                          onBlur: () => {},
                          ref: () => {},
                        }),
                        formState: formMethods.formState,
                        trigger: formMethods.trigger,
                        reset: formMethods.reset,
                        resetField: formMethods.resetField,
                        clearErrors: (name) => {
                          // Clear errors using the actual parent formMethods with the correct path
                          if (
                            name === nestedFieldKey ||
                            name === nestedFieldName
                          ) {
                            // Clear the nested field error
                            const nestedErrorPath = `${fieldName}.fields.${nestedFieldKey}`;
                            formMethods.clearErrors(nestedErrorPath);
                            // Also clear parent field error if it exists
                            formMethods.clearErrors(fieldName);
                          } else {
                            // Clear nested field errors within this nested radio_boolean_choice
                            const nestedErrorPath = `${fieldName}.fields.${nestedFieldKey}.fields.${name}`;
                            formMethods.clearErrors(nestedErrorPath);
                          }
                        },
                      }
                    : null;

                  // Create a form state structure that radio_boolean_choice expects
                  // Use just the field key, not the flattened path
                  const nestedFormStateForRadio = {
                    [nestedFieldKey]: nestedFieldValue,
                  };

                  // Recursively render radio_boolean_choice
                  return (
                    <div key={nestedFieldName} className="nested-field-wrapper">
                      {renderField({
                        field: {
                          ...nestedField,
                          name: nestedFieldKey, // Use just the key, not the flattened path
                        },
                        formState: nestedFormStateForRadio,
                        setFormState: (updater) => {
                          if (typeof updater === "function") {
                            const newState = updater(nestedFormStateForRadio);
                            const newValue = newState[nestedFieldKey];
                            nestedHandleValueChange(nestedFieldKey, newValue);
                          } else {
                            const newValue = updater[nestedFieldKey];
                            nestedHandleValueChange(nestedFieldKey, newValue);
                          }
                        },
                        handleToggleComposedField: () => {},
                        handleValueChange: (name, value) => {
                          nestedHandleValueChange(nestedFieldKey, value);
                        },
                        formMethods: nestedFormMethods,
                      })}
                    </div>
                  );
                } else if (nestedField.type === "multi_select") {
                  // Get the current value from nested fields state or formMethods
                  const currentValue = (() => {
                    if (formMethods) {
                      const parentValue = formMethods.getValues(fieldName);
                      if (
                        parentValue &&
                        typeof parentValue === "object" &&
                        parentValue.fields
                      ) {
                        return parentValue.fields[nestedFieldKey] || [];
                      }
                    }
                    return nestedFieldsState[nestedFieldKey] || [];
                  })();

                  // Check for errors at the nested path: fieldName.fields.nestedFieldKey
                  // The validation error path is ["fields", "exchanges"], so react-hook-form stores it at
                  // cryptoTransaction.fields.exchanges in the errors object
                  const nestedErrorPath = `${fieldName}.fields.${nestedFieldKey}`;
                  const parentError =
                    formMethods?.formState?.errors?.[fieldName];
                  const nestedError =
                    parentError?.fields?.[nestedFieldKey] ||
                    formMethods?.formState?.errors?.[nestedErrorPath];

                  return (
                    <div key={nestedFieldName} className="nested-field-wrapper">
                      <div className="form-field" style={wrapperStyle}>
                        <FieldLabel
                          label={nestedField.label}
                          description={nestedField.description}
                          htmlFor={nestedFieldName}
                          required={nestedField.required || false}
                        />
                        <MultiSelect
                          options={
                            nestedField.options ||
                            getSelectOptions(nestedField.option) ||
                            []
                          }
                          name={nestedFieldName}
                          onChange={(value) => {
                            nestedHandleValueChange(
                              nestedFieldKey,
                              value || []
                            );

                            // If "Next" has been clicked and user fixes the error, clear it immediately
                            if (hasAttemptedNext && formMethods) {
                              const selectedExchanges = value || [];
                              // If at least one exchange is selected, clear the error
                              if (selectedExchanges.length > 0) {
                                // Clear nested field error
                                const nestedErrorPath = `${fieldName}.fields.${nestedFieldKey}`;
                                formMethods.clearErrors(nestedErrorPath);
                                // Also clear parent field error if it exists
                                formMethods.clearErrors(fieldName);
                              }
                            }
                          }}
                          // Don't pass formMethods/fieldName - handle updates through onChange/value props
                          // The actual path in react-hook-form is cryptoTransaction.fields.exchanges,
                          // not cryptoTransaction.exchanges, so we handle it manually
                          placeholder={
                            nestedField.placeholder || "Select All That Apply"
                          }
                          disabled={nestedField.disabled || false}
                          value={currentValue}
                        />
                        {/* Only show errors if "Next" has been clicked */}
                        {nestedError && hasAttemptedNext && (
                          <ErrorLabel fieldName={nestedField.label}>
                            {nestedError.message ||
                              "Please select at least one exchange"}
                          </ErrorLabel>
                        )}
                      </div>
                    </div>
                  );
                }
                return null;
              })}
            </div>
          )}

          {/* Only show parent field error if there's no nested field error (to avoid duplicate messages) */}
          {/* For multi_boolean_choice with nested fields, we show nested errors instead of parent errors */}
          {formMethods &&
            formMethods.formState.errors[fieldName] &&
            hasAttemptedNext &&
            !(
              field.fields &&
              field.fields.some((nestedField) => {
                const nestedErrorPath = `${fieldName}.fields.${nestedField.name}`;
                const parentError = formMethods.formState.errors[fieldName];
                const hasNestedError =
                  parentError?.fields?.[nestedField.name] ||
                  formMethods.formState.errors[nestedErrorPath];
                return hasNestedError;
              })
            ) && (
              <ErrorLabel fieldName={field.label}>
                {formMethods.formState.errors[fieldName].message}
              </ErrorLabel>
            )}
        </div>
      );

    case "single_boolean_choice":
      if (Array.isArray(field.rich_label)) {
        return (
          <RichCheckboxField
            field={field}
            fieldName={fieldName}
            formState={formState}
            localHandleChange={localHandleChange}
            formMethods={formMethods}
            wrapperStyle={wrapperStyle}
            hasAttemptedNext={hasAttemptedNext}
          />
        );
      }
      return (
        <div key={fieldName} className="form-field" style={wrapperStyle}>
          <div className="checkbox-container">
            <div className="checkbox-item">
              <div className="checkbox-representation">
                <input
                  type="checkbox"
                  id={fieldName}
                  name={fieldName}
                  checked={(() => {
                    if (formMethods) {
                      const v = formMethods.getValues(fieldName);
                      // Handle null values for optional fields
                      if (v === null || v === undefined) return false;
                      return typeof v === "object" ? !!v?.value : !!v;
                    }
                    const fieldValue = formState[fieldName];
                    if (fieldValue === null || fieldValue === undefined)
                      return false;
                    return !!fieldValue?.value;
                  })()}
                  onChange={(e) => {
                    const next = !!e.target.checked;
                    if (formMethods) {
                      // Always use { value: boolean } structure for single_boolean_choice
                      // This ensures consistency with how data is loaded and saved
                      formMethods.setValue(fieldName, { value: next }, {
                        shouldValidate: false,
                        shouldDirty: true,
                      });
                    } else {
                      handleValueChange(fieldName, next);
                    }
                  }}
                  {...(formMethods
                    ? (() => {
                        // For optional single_boolean_choice fields, don't use react-hook-form's required validation
                        // The Zod schema will handle validation instead
                        // Only set required if field is explicitly marked as required
                        // For optional fields, provide a validate function that always returns true
                        // This prevents react-hook-form from running its own validation
                        const isRequired = field.required === true;
                        const rp = formMethods.register(fieldName, {
                          required: isRequired,
                          // For optional fields, always pass validation (Zod will handle it)
                          validate: isRequired ? undefined : () => true,
                        });
                        const {
                          ref: _ref,
                          onChange: _onChange,
                          ...rest
                        } = rp || {};
                        return rest;
                      })()
                    : {})}
                />
                <FieldLabel
                  label={field.label}
                  description={field.description}
                  htmlFor={fieldName}
                />
              </div>
            </div>
          </div>
          {/* Only show errors if "Next" has been clicked */}
          {formMethods &&
            formMethods.formState.errors[fieldName] &&
            hasAttemptedNext && (
              <ErrorLabel fieldName={field.label}>
                {formMethods.formState.errors[fieldName].message}
              </ErrorLabel>
            )}
        </div>
      );

    case "multi_address":
      return (
        <div key={fieldName} style={wrapperStyle} className="form-field">
          {field.label && (
            <FieldLabel
              label={field.label}
              description={field.description}
              required={field.required || false}
            />
          )}
          {(() => {
            // Get taxYears from form state (may be from Tax_Basics section)
            // Try to get from formMethods first, then from formState
            let taxYears = null;
            if (formMethods) {
              const taxYearsValue = formMethods.getValues("taxYears");
              if (Array.isArray(taxYearsValue) && taxYearsValue.length > 0) {
                // Convert to numbers if they're strings
                taxYears = taxYearsValue.map((year) =>
                  typeof year === "string" ? parseInt(year, 10) : year
                );
              }
            } else if (formState && formState.taxYears) {
              const taxYearsValue = formState.taxYears;
              if (Array.isArray(taxYearsValue) && taxYearsValue.length > 0) {
                taxYears = taxYearsValue.map((year) =>
                  typeof year === "string" ? parseInt(year, 10) : year
                );
              }
            }

            let registerProps = {};
            // Don't register here - it causes infinite loops
            // Registration will be handled by the manual validation in VerticalFormWizard

            // Create a wrapper function that syncs with both formState and formMethods
            const syncFormState = (updater) => {
              if (typeof updater === "function") {
                const newState = updater(formState);
                const newValue = newState[fieldName];
                // Sync with formMethods for validation
                if (formMethods) {
                  formMethods.setValue(fieldName, newValue, {
                    shouldValidate: true,
                    shouldDirty: true,
                  });
                }
                // Update local form state
                setFormState(newState);
              } else {
                // Direct value update
                const newValue = updater[fieldName];
                // Sync with formMethods for validation
                if (formMethods) {
                  formMethods.setValue(fieldName, newValue, {
                    shouldValidate: true,
                    shouldDirty: true,
                  });
                }
                // Update local form state
                setFormState(updater);
              }
            };

            return (
              <MultiAddress
                addresses={formState[fieldName] || []}
                setFormState={syncFormState}
                description={field.description}
                fieldName={fieldName}
                taxYears={taxYears}
                formMethods={formMethods}
                {...registerProps}
              />
            );
          })()}
          {/* Only show errors if "Next" has been clicked */}
          {formMethods &&
            formMethods.formState.errors[fieldName] &&
            hasAttemptedNext && (
              <ErrorLabel fieldName={field.label}>
                {formMethods.formState.errors[fieldName].message}
              </ErrorLabel>
            )}
        </div>
      );

    case "single_address":
      return (
        <div key={fieldName} style={wrapperStyle} className="form-field">
          {field.label && (
            <FieldLabel
              label={field.label}
              description={field.description}
              required={field.required || false}
            />
          )}
          {(() => {
            let registerProps = {};
            if (formMethods) {
              const rp = formMethods.register(fieldName, {
                required: field.required || false,
              });
              const { ref: _ref, ...rest } = rp || {};
              registerProps = rest;
            }

            // Create a wrapper function that syncs with both formState and formMethods
            const syncFormState = (updater) => {
              if (typeof updater === "function") {
                const newState = updater(formState);
                const newValue = newState[fieldName];
                // Sync with formMethods for validation
                if (formMethods) {
                  formMethods.setValue(fieldName, newValue, {
                    shouldValidate: true,
                    shouldDirty: true,
                  });
                }
                // Update local form state
                setFormState(newState);
              } else {
                // Direct value update
                const newValue = updater[fieldName];
                // Sync with formMethods for validation
                if (formMethods) {
                  formMethods.setValue(fieldName, newValue, {
                    shouldValidate: true,
                    shouldDirty: true,
                  });
                }
                // Update local form state
                setFormState(updater);
              }
            };

            return (
              <SingleAddress
                formState={formState}
                setFormState={syncFormState}
                fieldNames={getAddressFieldNames(fieldName)}
                formMethods={formMethods}
                fieldName={fieldName}
                {...registerProps}
              />
            );
          })()}
          {/* Only show errors if "Next" has been clicked */}
          {formMethods &&
            formMethods.formState.errors[fieldName] &&
            hasAttemptedNext && (
              <ErrorLabel fieldName={field.label}>
                {formMethods.formState.errors[fieldName].message}
              </ErrorLabel>
            )}
        </div>
      );

    /**
     * Native form elements
     */
    case "textarea":
      return (
        <div key={fieldName} style={wrapperStyle} className="form-field">
          {field.label && (
            <FieldLabel
              label={field.label}
              description={field.description}
              htmlFor={fieldName}
            />
          )}
          <textarea
            rows="15"
            className="form-textarea"
            name={fieldName}
            defaultValue={formState.description}
            placeholder="Type here..."
            onChange={(e) => localHandleChange(e.target.value)}
            {...(formMethods ? formMethods.register(fieldName) : {})}
          ></textarea>
          {/* Only show errors if "Next" has been clicked */}
          {formMethods &&
            formMethods.formState.errors[fieldName] &&
            hasAttemptedNext && (
              <ErrorLabel fieldName={field.label}>
                {formMethods.formState.errors[fieldName].message}
              </ErrorLabel>
            )}
        </div>
      );

    case "radio_boolean_choice":
      // Radio buttons with conditional nested fields
      const selectedRadioValue = (() => {
        if (formMethods) {
          const v = formMethods.getValues(fieldName);
          return typeof v === "object" ? v?.value : v;
        }
        return formState[fieldName]?.value || "";
      })();

      // Handle nested fields form state
      const radioNestedFieldsState = formState[fieldName]?.fields || {};
      const setRadioNestedFieldsState = (updater) => {
        if (typeof updater === "function") {
          const newNestedState = updater(radioNestedFieldsState);
          const newValue = {
            value: selectedRadioValue,
            fields: newNestedState,
          };
          if (formMethods) {
            formMethods.setValue(fieldName, newValue, {
              shouldValidate: false,
              shouldDirty: true,
            });
          }
          setFormState((prev) => ({
            ...prev,
            [fieldName]: newValue,
          }));
        } else {
          const newValue = {
            value: selectedRadioValue,
            fields: updater,
          };
          if (formMethods) {
            formMethods.setValue(fieldName, newValue, {
              shouldValidate: false,
              shouldDirty: true,
            });
          }
          setFormState((prev) => ({
            ...prev,
            [fieldName]: newValue,
          }));
        }
      };

      // Find the selected option
      const selectedOption = field.options?.find(
        (opt) => opt.value === selectedRadioValue
      );

      return (
        <div key={fieldName} className="form-field" style={wrapperStyle}>
          {field.label && (
            <FieldLabel
              label={field.label}
              description={field.description}
              required={field.required || false}
            />
          )}

          {/* Radio buttons */}
          <div className="checkbox-container radio-buttons-container">
            {field.options?.map((option, index) => {
              const radioId = `${fieldName}_${option.value}`;
              return (
                <div className="checkbox-item" key={radioId}>
                  <div className="checkbox-representation">
                    <input
                      type="radio"
                      id={radioId}
                      name={fieldName}
                      value={option.value}
                      checked={selectedRadioValue === option.value}
                      onChange={(e) => {
                        const newValue = {
                          value: e.target.value,
                          fields: {},
                        };
                        if (formMethods) {
                          formMethods.setValue(fieldName, newValue, {
                            shouldValidate: true,
                            shouldDirty: true,
                          });
                        } else {
                          setFormState((prev) => ({
                            ...prev,
                            [fieldName]: newValue,
                          }));
                        }
                      }}
                      {...(formMethods
                        ? (() => {
                            const rp = formMethods.register(fieldName, {
                              required: field.required || false,
                            });
                            const {
                              ref: _ref,
                              onChange: _onChange,
                              ...rest
                            } = rp || {};
                            return rest;
                          })()
                        : {})}
                    />
                    <label htmlFor={radioId}>{option.label}</label>
                  </div>
                </div>
              );
            })}
          </div>

          {/* Render nested fields for selected option */}
          {selectedOption &&
            selectedOption.fields &&
            Array.isArray(selectedOption.fields) && (
              <div style={{ marginLeft: "24px", marginTop: "16px" }}>
                {selectedOption.fields.map((nestedField, index) => {
                  const nestedFieldKey = nestedField.name || `field_${index}`;
                  // Use just the simple field name, not the full path
                  // The custom formMethods will handle the nested structure
                  const nestedFieldName = nestedFieldKey;

                  // Create handlers for nested fields
                  const nestedHandleValueChange = (name, value) => {
                    setRadioNestedFieldsState((prev) => ({
                      ...prev,
                      [name]: value,
                    }));
                  };

                  const nestedSetFormState = (updater) => {
                    if (typeof updater === "function") {
                      setRadioNestedFieldsState(updater);
                    } else {
                      setRadioNestedFieldsState(updater);
                    }
                  };

                  // Create a custom formMethods for nested fields within radio_boolean_choice
                  // This ensures nested fields use simple names, not flattened paths
                  const nestedFieldFormMethods = formMethods
                    ? {
                        ...formMethods,
                        getValues: (path) => {
                          // For nested fields, read from radioNestedFieldsState
                          if (
                            path === nestedFieldKey ||
                            path === nestedFieldName
                          ) {
                            return (
                              radioNestedFieldsState[nestedFieldKey] || {
                                value: false,
                              }
                            );
                          }
                          return formMethods.getValues(path);
                        },
                        setValue: (path, value, options) => {
                          // For nested fields, update radioNestedFieldsState
                          if (
                            path === nestedFieldKey ||
                            path === nestedFieldName
                          ) {
                            nestedHandleValueChange(nestedFieldKey, value);
                          } else {
                            formMethods.setValue(path, value, options);
                          }
                        },
                        watch: (path) => {
                          if (
                            path === nestedFieldKey ||
                            path === nestedFieldName
                          ) {
                            return (
                              radioNestedFieldsState[nestedFieldKey] || {
                                value: false,
                              }
                            );
                          }
                          return formMethods.watch(path);
                        },
                        register: (path) => ({
                          name: path,
                          onChange: (e) => {
                            const value =
                              e.target.type === "checkbox"
                                ? e.target.checked
                                : e.target.value;
                            if (
                              path === nestedFieldKey ||
                              path === nestedFieldName
                            ) {
                              nestedHandleValueChange(nestedFieldKey, {
                                value,
                              });
                            } else {
                              formMethods.register(path).onChange(e);
                            }
                          },
                          onBlur: () => {},
                          ref: () => {},
                        }),
                        formState: formMethods.formState,
                        trigger: formMethods.trigger,
                        reset: formMethods.reset,
                        resetField: formMethods.resetField,
                      }
                    : null;

                  if (nestedField.type === "single_boolean_choice") {
                    // Create a form state structure that single_boolean_choice expects
                    const nestedFormStateForCheckbox = {
                      [nestedFieldName]: radioNestedFieldsState[
                        nestedFieldKey
                      ] || { value: false },
                    };

                    // Recursively render single_boolean_choice
                    return (
                      <div
                        key={nestedFieldName}
                        className="nested-field-wrapper"
                      >
                        {renderField({
                          field: {
                            ...nestedField,
                            name: nestedFieldName, // Use simple name
                          },
                          formState: nestedFormStateForCheckbox,
                          setFormState: (updater) => {
                            if (typeof updater === "function") {
                              const newState = updater(
                                nestedFormStateForCheckbox
                              );
                              const newValue = newState[nestedFieldName];
                              nestedHandleValueChange(nestedFieldKey, newValue);
                            } else {
                              const newValue = updater[nestedFieldName];
                              nestedHandleValueChange(nestedFieldKey, newValue);
                            }
                          },
                          handleToggleComposedField: () => {},
                          handleValueChange: (name, value) => {
                            nestedHandleValueChange(nestedFieldKey, { value });
                          },
                          formMethods: nestedFieldFormMethods,
                        })}
                      </div>
                    );
                  }
                  return null;
                })}
              </div>
            )}

          {/* Only show errors if "Next" has been clicked */}
          {formMethods &&
            formMethods.formState.errors[fieldName] &&
            hasAttemptedNext && (
              <ErrorLabel fieldName={field.label}>
                {formMethods.formState.errors[fieldName].message}
              </ErrorLabel>
            )}
        </div>
      );

    case "radio_group":
      // Get current value from react-hook-form if available, otherwise from formState
      const currentRadioValue = formMethods
        ? formMethods.watch(fieldName) || formMethods.getValues(fieldName)
        : formState[fieldName];

      return (
        <div key={fieldName} style={wrapperStyle} className="form-field">
          {field.display_as_heading ? (
            <h4
              style={{
                marginBottom: 8,
                display: "flex",
                alignItems: "center",
                gap: "8px",
              }}
            >
              {field.label}
              {field.description && (
                <>
                  <Tooltip
                    title={field.titleTooltip}
                    content={field.description}
                  />
                </>
              )}
            </h4>
          ) : (
            <FieldLabel
              label={field.label}
              description={field.description}
              titleTooltip={field.titleTooltip}
              required={field.required || false}
            />
          )}
          <RadioGroup
            name={fieldName}
            options={field.options || getSelectOptions(field.option) || []}
            selectedValue={currentRadioValue}
            onChange={(value) => {
              // Update form state
              localHandleChange(value);
              // Also trigger validation
              if (formMethods) {
                formMethods.setValue(fieldName, value, {
                  shouldValidate: true,
                  shouldDirty: true,
                });
              }
            }}
            isDisabled={false}
          />
          {/* Register with react-hook-form for validation */}
          {formMethods && (
            <input
              type="hidden"
              {...formMethods.register(fieldName, {
                required: field.required || false,
              })}
            />
          )}
          {/* Only show errors if "Next" has been clicked */}
          {formMethods &&
            formMethods.formState.errors[fieldName] &&
            hasAttemptedNext && (
              <ErrorLabel fieldName={field.label}>
                {formMethods.formState.errors[fieldName].message}
              </ErrorLabel>
            )}
        </div>
      );

    case "input":
      // New form structure with password toggle functionality
      if ((field.input_type || "text").toLowerCase() === "password") {
        return (
          <PasswordField
            field={field}
            fieldName={fieldName}
            formState={formState}
            localHandleChange={localHandleChange}
            formMethods={formMethods}
            wrapperStyle={wrapperStyle}
            hasAttemptedNext={hasAttemptedNext}
          />
        );
      }

      if ((field.input_type || "text").toLowerCase() === "date") {
        return (
          <div key={fieldName} className="form-field" style={wrapperStyle}>
            <FieldLabel
              label={field.label}
              description={field.description}
              htmlFor={fieldName}
              required={field.required || false}
            />
            <DateInput
              name={fieldName}
              label={field.label}
              placeholder={field.placeholder || "mm/dd/yyyy"}
              required={field.required || false}
              maxDate={new Date()}
              value={
                formMethods
                  ? formMethods.getValues(fieldName) || ""
                  : formState[fieldName] || ""
              }
              onChange={(e) => {
                const value = e.target.value;
                if (formMethods) {
                  // Use setValue with shouldValidate to trigger validation immediately
                  formMethods.setValue(fieldName, value, {
                    shouldValidate: false,
                    shouldTouch: true,
                    shouldDirty: true,
                  });
                } else {
                  localHandleChange(value);
                }
              }}
              onBlur={(e) => {
                // Also trigger validation on blur
                if (formMethods && e.target.name) {
                  formMethods.trigger(e.target.name, { shouldFocus: false });
                }
              }}
              {...(formMethods
                ? (() => {
                    // Get register props but override onChange and onBlur
                    const {
                      onChange: _onChange,
                      onBlur: _onBlur,
                      ...rest
                    } = formMethods.register(fieldName, {
                      required: field.required || false,
                    });
                    return rest; // Only pass name and ref from register
                  })()
                : {})}
            />
            {/* Only show errors if "Next" has been clicked AND value is actually empty */}
            {formMethods &&
              formMethods.formState.errors[fieldName] &&
              hasAttemptedNext &&
              !formMethods.getValues(fieldName) && (
                <ErrorLabel
                  fieldName={field.label || getDefaultLabel(fieldName)}
                >
                  {formMethods.formState.errors[fieldName].message}
                </ErrorLabel>
              )}
          </div>
        );
      }

      // Check if this is a phone field
      const isPhoneField =
        field.type === "input" &&
        field.input_type === "tel" &&
        field.name === "phone";

      // Determine placeholder: use field.placeholder if provided, otherwise empty (except phone gets default)
      const getPlaceholder = () => {
        if (field.placeholder !== undefined) {
          return field.placeholder;
        }
        // For phone, default to format if no placeholder specified
        if (isPhoneField) {
          return "(###)-###-####";
        }
        return "";
      };

      // Use PhoneInput component for phone fields
      if (isPhoneField) {
        return (
          <div key={fieldName} className="form-field" style={wrapperStyle}>
            <FieldLabel
              label={field.label}
              description={field.description}
              htmlFor={fieldName}
              required={field.required || false}
            />
            <PhoneInput
              name={fieldName}
              required={field.required || false}
              {...(formMethods
                ? (() => {
                    const registerResult = formMethods.register(fieldName, {
                      required: field.required || false,
                      pattern: field.input_pattern
                        ? new RegExp(field.input_pattern)
                        : undefined,
                      minLength: field.minLength,
                      maxLength: field.maxLength,
                    });

                    const { onChange, onBlur, ...registerProps } =
                      registerResult;
                    // Get the stored value (digits-only for validation)
                    // PhoneInput will format it for display internally
                    const storedValue = formMethods.getValues(fieldName) || "";
                    return {
                      ...registerProps,
                      value: storedValue, // Pass digits-only, PhoneInput formats it for display
                      onChange: (e) => {
                        // PhoneInput already sends digits-only value, use it directly
                        const digitsOnly = e.target.value;
                        // Call the original onChange with digits-only for validation
                        onChange({
                          target: { value: digitsOnly, name: fieldName },
                        });
                        // Also update via setValue with digits-only to ensure watch subscription detects the change
                        formMethods.setValue(fieldName, digitsOnly, {
                          shouldValidate: false,
                          shouldDirty: true,
                          shouldTouch: true, // Mark as touched to ensure validation runs
                        });
                      },
                      onBlur: (e) => {
                        if (onBlur) {
                          onBlur(e);
                        }
                      },
                    };
                  })()
                : {
                    value: formState[fieldName] || "",
                    onChange: (e) => localHandleChange(e.target.value),
                  })}
            />
            {/* Only show errors if "Next" has been clicked */}
            {formMethods &&
              formMethods.formState.errors[fieldName] &&
              hasAttemptedNext && (
                <ErrorLabel fieldName={field.label}>
                  {formMethods.formState.errors[fieldName].message}
                </ErrorLabel>
              )}
          </div>
        );
      }

      return (
        <div key={fieldName} className="form-field" style={wrapperStyle}>
          {!field.hideLabel && (
            <FieldLabel
              label={field.label}
              description={field.description}
              htmlFor={fieldName}
              required={field.required || false}
            />
          )}
          {field.icon ? (
            <div className="email-input-container">
              <input
                type={field.input_type || "text"}
                name={fieldName}
                className="form-input"
                placeholder={getPlaceholder()}
                required={field.required || false}
                pattern={field.input_pattern || undefined}
                minLength={field.minLength || undefined}
                maxLength={field.maxLength || undefined}
                {...(formMethods
                  ? formMethods.register(fieldName, {
                      required: field.required || false,
                      pattern: field.input_pattern
                        ? new RegExp(field.input_pattern)
                        : undefined,
                      minLength: field.minLength,
                      maxLength: field.maxLength,
                    })
                  : {
                      value: formState[fieldName] || "",
                      onChange: (e) => localHandleChange(e.target.value),
                    })}
              />
              <img
                src={field.icon}
                alt={field.label}
                className="email-icon"
              />
            </div>
          ) : (
            <input
              type={field.input_type || "text"}
              name={fieldName}
              className="form-input"
              placeholder={getPlaceholder()}
              required={field.required || false}
              pattern={field.input_pattern || undefined}
              minLength={field.minLength || undefined}
              maxLength={field.maxLength || undefined}
              {...(formMethods
                ? formMethods.register(fieldName, {
                    required: field.required || false,
                    pattern: field.input_pattern
                      ? new RegExp(field.input_pattern)
                      : undefined,
                    minLength: field.minLength,
                    maxLength: field.maxLength,
                  })
                : {
                    value: formState[fieldName] || "",
                    onChange: (e) => localHandleChange(e.target.value),
                  })}
            />
          )}
          {/* Only show errors if "Next" has been clicked */}
          {formMethods &&
            formMethods.formState.errors[fieldName] &&
            hasAttemptedNext && (
              <ErrorLabel fieldName={field.label}>
                {formMethods.formState.errors[fieldName].message}
              </ErrorLabel>
            )}
        </div>
      );

    case "numeric":
      // Numeric field (integer or currency) - can be top-level or nested
      // Uses the same rendering structure as nested numeric fields
      const isInteger = field.inputType === "integer";
      const storedValue = formMethods
        ? formMethods.getValues(fieldName)
        : formState[fieldName];
      let displayValue = "";
      const numericMinValue =
        field.min !== undefined ? field.min : isInteger ? 1 : undefined;

      if (storedValue === null || storedValue === undefined) {
        // Default integer fields to 0, currency fields to empty
        displayValue = isInteger ? "0" : "";
      } else if (storedValue === "") {
        // Allow empty string while user is typing (can be cleared)
        displayValue = "";
      } else if (typeof storedValue === "number") {
        // Format number for display
        displayValue = isInteger
          ? Math.floor(storedValue).toString()
          : storedValue.toFixed(2);
      } else {
        // If it's a string, use it directly (while typing)
        displayValue = storedValue;
      }

      // Currency input rendering (same structure as nested)
      if (!isInteger) {
        return (
          <div key={fieldName} className="form-field" style={wrapperStyle}>
            <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
              <div style={{ position: "relative", width: "120px" }}>
                <span
                  style={{
                    position: "absolute",
                    left: "8px",
                    top: "50%",
                    transform: "translateY(-50%)",
                    color: "#666b6f",
                    fontSize: "14px",
                    pointerEvents: "none",
                  }}
                >
                  $
                </span>
                <input
                  type="text"
                  id={fieldName}
                  name={fieldName}
                  className="form-input"
                  style={{
                    paddingLeft: "24px",
                    width: "100%",
                  }}
                  value={displayValue}
                  onChange={(e) => {
                    const inputValue = e.target.value;
                    // Remove all non-numeric characters except decimal point
                    const cleaned = inputValue.replace(/[^\d.]/g, "");
                    // Allow only one decimal point
                    const parts = cleaned.split(".");
                    let formatted =
                      parts.length > 2
                        ? parts[0] + "." + parts.slice(1).join("")
                        : cleaned;

                    // Limit to 2 decimal places
                    if (formatted.includes(".")) {
                      const [integer, decimal] = formatted.split(".");
                      formatted =
                        integer + "." + (decimal ? decimal.slice(0, 2) : "");
                    }

                    // Store as string while typing
                    if (formMethods) {
                      formMethods.setValue(fieldName, formatted, {
                        shouldValidate: false,
                        shouldDirty: true,
                      });
                    } else {
                      localHandleChange(formatted);
                    }
                  }}
                  onBlur={(e) => {
                    // Format to 2 decimal places and convert to number on blur
                    const value = e.target.value.trim();
                    let finalValue = "";

                    if (value === "") {
                      finalValue = "";
                    } else if (
                      !isNaN(parseFloat(value)) &&
                      parseFloat(value) >= 0
                    ) {
                      const numValue = parseFloat(value);
                      const formatted = numValue.toFixed(2);
                      // Store as number after formatting
                      finalValue = parseFloat(formatted);
                    } else {
                      // Invalid value, clear it
                      finalValue = "";
                    }

                    if (formMethods) {
                      formMethods.setValue(fieldName, finalValue, {
                        shouldValidate: false,
                        shouldDirty: true,
                      });
                    } else {
                      localHandleChange(finalValue);
                    }
                  }}
                  placeholder="0.00"
                  {...(formMethods
                    ? (() => {
                        const registerResult = formMethods.register(fieldName, {
                          required: field.required || false,
                          validate: (value) => {
                            // Get the actual value from form state
                            const actualValue =
                              formMethods.getValues(fieldName);
                            if (
                              field.required &&
                              (!actualValue || actualValue === "")
                            ) {
                              return `${field.label} is required`;
                            }
                            const numValue =
                              typeof actualValue === "number"
                                ? actualValue
                                : parseFloat(actualValue);
                            if (
                              actualValue &&
                              (isNaN(numValue) || numValue < 0)
                            ) {
                              return `${field.label} must be a valid positive number`;
                            }
                            return true;
                          },
                        });

                        // Return only name and ref, we handle onChange/onBlur ourselves
                        const {
                          onChange: _onChange,
                          onBlur: _onBlur,
                          ...rest
                        } = registerResult || {};
                        return rest;
                      })()
                    : {})}
                />
              </div>
              <FieldLabel
                label={field.label}
                description={field.description}
                htmlFor={fieldName}
                className=""
              />
            </div>
            {/* Only show errors if "Next" has been clicked */}
            {formMethods &&
              formMethods.formState.errors[fieldName] &&
              hasAttemptedNext && (
                <ErrorLabel fieldName={field.label}>
                  {formMethods.formState.errors[fieldName].message}
                </ErrorLabel>
              )}
          </div>
        );
      }

      // Integer input rendering (same structure as nested)
      const minValue = field.min !== undefined ? field.min : 1;
      const maxValue = field.max !== undefined ? field.max : undefined;

      return (
        <div key={fieldName} className="form-field" style={wrapperStyle}>
          <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
            <div style={{ width: "120px" }}>
              <input
                type="number"
                id={fieldName}
                name={fieldName}
                className="form-input"
                style={{
                  width: "100%",
                }}
                min={minValue}
                max={maxValue}
                step="1"
                value={displayValue}
                onChange={(e) => {
                  const inputValue = e.target.value;

                  // Allow empty string while user is typing/clearing
                  if (inputValue === "") {
                    if (formMethods) {
                      formMethods.setValue(fieldName, "", {
                        shouldValidate: false,
                        shouldDirty: true,
                      });
                    } else {
                      localHandleChange("");
                    }
                    return;
                  }

                  // Only allow positive integers
                  const cleaned = inputValue.replace(/[^\d]/g, "");
                  let numValue;

                  if (cleaned === "") {
                    // Allow empty while typing, will default to minValue on blur
                    numValue = "";
                  } else {
                    const parsed = parseInt(cleaned, 10);
                    // Only enforce minimum if user has entered a valid number
                    // Don't force to minValue while they're still typing
                    if (isNaN(parsed)) {
                      numValue = "";
                    } else if (maxValue !== undefined && parsed > maxValue) {
                      numValue = maxValue;
                    } else if (parsed < minValue) {
                      numValue = "";
                    } else {
                      numValue = parsed;
                    }
                  }

                  // Store as number (or empty string while typing)
                  if (formMethods) {
                    formMethods.setValue(fieldName, numValue, {
                      shouldValidate: false,
                      shouldDirty: true,
                    });
                  } else {
                    localHandleChange(numValue);
                  }
                }}
                onBlur={(e) => {
                  const value = e.target.value.trim();
                  let finalValue = 0; // Default to 0 for integer fields

                  if (value === "") {
                    finalValue = 0; // Default to 0 if empty
                  } else if (!isNaN(parseInt(value, 10))) {
                    finalValue = parseInt(value, 10);
                    // Ensure value is within min/max range
                    if (finalValue < minValue) {
                      finalValue = minValue;
                    } else if (
                      maxValue !== undefined &&
                      finalValue > maxValue
                    ) {
                      finalValue = maxValue;
                    }
                  } else {
                    finalValue = 0; // Default to 0 if invalid
                  }

                  if (formMethods) {
                    formMethods.setValue(fieldName, finalValue, {
                      shouldValidate: false,
                      shouldDirty: true,
                    });
                  } else {
                    localHandleChange(finalValue);
                  }
                }}
                placeholder="0"
                {...(formMethods
                  ? (() => {
                      const registerResult = formMethods.register(fieldName, {
                        required: field.required || false,
                        validate: (value) => {
                          // Get the actual value from form state
                          const actualValue = formMethods.getValues(fieldName);
                          if (
                            field.required &&
                            (actualValue === null ||
                              actualValue === undefined ||
                              actualValue === "" ||
                              actualValue === 0)
                          ) {
                            return `${field.label} is required`;
                          }
                          const numValue =
                            typeof actualValue === "number"
                              ? actualValue
                              : parseInt(actualValue, 10);
                          if (
                            actualValue &&
                            (isNaN(numValue) || numValue < minValue)
                          ) {
                            return `${field.label} must be at least ${minValue}`;
                          }
                          if (maxValue !== undefined && numValue > maxValue) {
                            return `${field.label} must be at most ${maxValue}`;
                          }
                          return true;
                        },
                      });

                      // Return only name and ref, we handle onChange/onBlur ourselves
                      const {
                        onChange: _onChange,
                        onBlur: _onBlur,
                        ...rest
                      } = registerResult || {};
                      return rest;
                    })()
                  : {})}
              />
            </div>
            <FieldLabel
              label={field.label}
              description={field.description}
              htmlFor={fieldName}
              className=""
            />
          </div>
          {/* Only show errors if "Next" has been clicked */}
          {formMethods &&
            formMethods.formState.errors[fieldName] &&
            hasAttemptedNext && (
              <ErrorLabel fieldName={field.label}>
                {formMethods.formState.errors[fieldName].message}
              </ErrorLabel>
            )}
        </div>
      );

    default:
      return null;
  }
};
